import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, UserPlus, Mail, KeyRound, Ticket } from "lucide-react";
import raven from "@/assets/raven.png";
import { useAuth } from "@/lib/auth";
import { api, formatApiError, setToken } from "@/lib/api";

// read a reset token from the URL (?reset_token=...)
const RESET_TOKEN = new URLSearchParams(window.location.search).get("reset_token");

function clearResetParam() {
  const u = new URL(window.location.href);
  u.searchParams.delete("reset_token");
  window.history.replaceState({}, "", u.toString());
}

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(RESET_TOKEN ? "reset" : "login"); // login | register | forgot | reset
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [betaCode, setBetaCode] = useState("");
  const [betaEnabled, setBetaEnabled] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    api.betaStatus().then((r) => setBetaEnabled(!!r.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    try {
      if (mode === "login") await login(username, password);
      else if (mode === "register") await register(username, password, email, betaCode);
      else if (mode === "forgot") {
        const r = await api.forgot(email, window.location.origin);
        setMsg("If that email is registered, a reset link is on its way. Check your inbox (including spam).");
        setCooldown(r?.cooldown || 60);
      } else if (mode === "reset") {
        await api.resetPassword(RESET_TOKEN, password);
        setToken(null);
        clearResetParam();
        setMsg("Password updated — you can now sign in.");
        setMode("login");
      }
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const title = { login: "SIGN IN", register: "CREATE ACCOUNT", forgot: "RESET PASSWORD", reset: "SET NEW PASSWORD" }[mode];

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative text-slate-100">
      <div className="fixed inset-0 tac-grid opacity-40 pointer-events-none" />
      <Card className="bg-[#121824]/80 border-slate-800 p-8 w-full max-w-md relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <img src={raven} alt="Spakr Hrafn raven" className="h-12 w-12 object-contain shrink-0" data-testid="auth-logo" />
          <div>
            <h1 className="font-brand font-semibold uppercase tracking-wide text-2xl leading-none text-slate-100">Spakr Hrafn</h1>
            <p className="text-[11px] text-slate-500 font-mono tracking-wider mt-0.5">TEAM PAIRING RESOLVER · {title}</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {(mode === "login" || mode === "register") && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Username</Label>
              <Input data-testid="auth-username-input" value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder="username" autoComplete="username" className="bg-slate-950/60 border-slate-700" />
            </div>
          )}
          {(mode === "register" || mode === "forgot") && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1"><Mail className="h-3 w-3" /> Email {mode === "register" && <span className="text-slate-600 normal-case">(for password recovery)</span>}</Label>
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" autoComplete="email" className="bg-slate-950/60 border-slate-700" />
            </div>
          )}
          {mode === "register" && betaEnabled && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1"><Ticket className="h-3 w-3" /> Beta access code</Label>
              <Input data-testid="auth-beta-code-input" value={betaCode} onChange={(e) => setBetaCode(e.target.value)}
                placeholder="enter your invite code" autoComplete="off" className="bg-slate-950/60 border-slate-700 font-mono tracking-widest uppercase" />
            </div>
          )}
          {(mode === "login" || mode === "register" || mode === "reset") && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 uppercase tracking-wider">{mode === "reset" ? "New password" : "Password"}</Label>
              <Input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="password" autoComplete={mode === "login" ? "current-password" : "new-password"} className="bg-slate-950/60 border-slate-700" />
            </div>
          )}
          {err && <p data-testid="auth-error" className="text-sm text-red-400">{err}</p>}
          {msg && <p data-testid="auth-message" className="text-sm text-emerald-400">{msg}</p>}
          {mode === "register" && !err && <p className="text-[11px] text-slate-500">Username min 3 · password min 4 characters. Email is optional but needed to recover your account.</p>}

          <Button data-testid="auth-submit-button" type="submit" disabled={busy || (mode === "forgot" && cooldown > 0)}
            className="w-full bg-amber-500 text-slate-900 hover:bg-amber-400 font-bold disabled:opacity-60">
            {mode === "login" ? <LogIn className="h-4 w-4 mr-2" /> : mode === "register" ? <UserPlus className="h-4 w-4 mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
            {busy ? "Please wait…"
              : mode === "forgot" && cooldown > 0 ? `Resend in ${cooldown}s`
              : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : "Set new password"}
          </Button>
        </form>

        <div className="mt-4 flex flex-col items-center gap-1.5 text-sm">
          {mode === "login" && (
            <>
              <button data-testid="auth-toggle-mode" onClick={() => { setMode("register"); setErr(""); setMsg(""); }} className="text-cyan-400 hover:text-cyan-300">No account? Register</button>
              <button data-testid="auth-forgot-link" onClick={() => { setMode("forgot"); setErr(""); setMsg(""); }} className="text-slate-400 hover:text-slate-200 text-xs">Forgot password?</button>
            </>
          )}
          {(mode === "register" || mode === "forgot" || mode === "reset") && (
            <button data-testid="auth-back-login" onClick={() => { setMode("login"); setErr(""); setMsg(""); }} className="text-cyan-400 hover:text-cyan-300">Back to sign in</button>
          )}
        </div>
      </Card>
    </div>
  );
}
